import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  InputAdornment,
  CircularProgress,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Paper,
  Box,
} from '@mui/material';
import { Search } from 'lucide-react';
import { getEquipments } from 'src/queries/query'; // Assuming this query exists

export default function AddEquipmentModal({
  open,
  handleClose,
  leg,
  onAssign,
}) {
  const [loading, setLoading] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState({}); // { uuid: true/false }

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setSearchTerm('');
      setSelectedEquipment({});
      fetchEquipment();
    }
  }, [open]);

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const result = await getEquipments(); // Assuming getEquipments fetches all available equipment
      const fetchedEquipment = Array.isArray(result?.data?.data?.data)
        ? result?.data?.data?.data
        : [];
      setEquipmentList(fetchedEquipment);
    } catch (error) {
      console.error('Error fetching equipment:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredEquipment = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return equipmentList.filter((equipment) => {
      const name =
        typeof equipment?.name === 'string' ? equipment.name.toLowerCase() : '';
      const type =
        typeof equipment?.equipmentType?.name === 'string'
          ? equipment.equipmentType.name.toLowerCase()
          : '';
      return name.includes(lowerSearch) || type.includes(lowerSearch);
    });
  }, [equipmentList, searchTerm]);

  const handleCheckboxChange = (uuid) => (event) => {
    setSelectedEquipment((prev) => ({
      ...prev,
      [uuid]: event.target.checked,
    }));
  };

  const handleAssignEquipment = () => {
    const assignedEquipmentUuids = Object.keys(selectedEquipment).filter(
      (uuid) => selectedEquipment[uuid]
    );
    console.log('assignedEquipmentUuids', assignedEquipmentUuids);
    if (assignedEquipmentUuids.length > 0) {
      onAssign(leg?.uuid, assignedEquipmentUuids);
    } else {
      console.log('Please select at least one equipment.');
      // Optionally show a toast or alert
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Assign Equipment to Leg: {leg?.legNumber}</DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 0 }}>
        <div className="mx-auto p-4">
          <p className="text-sm text-gray-700 mb-2">
            Search for equipment by name or type.
          </p>

          <div className="relative mb-6">
            <Input
              type="text"
              placeholder="Search equipment..."
              fullWidth
              sx={{ mb: 2, borderRadius: '8px' }} // Add some margin bottom and border radius
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <Search className="h-4 w-4 text-gray-400 mr-2" />
                </InputAdornment>
              }
            />
          </div>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            {searchTerm ? 'Search results' : 'Available Equipment'}
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-2">
              {' '}
              {/* Increased gap and added right padding for scrollbar */}
              {filteredEquipment.length > 0 ? (
                <FormGroup>
                  {filteredEquipment.map((equipment) => (
                    <Paper
                      key={equipment.uuid}
                      sx={{
                        p: 1,
                        px: 2,
                        border: '1px solid #e0e0e0',
                        mx: 1,
                        mb: 1,
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)',
                        },
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedEquipment[equipment.uuid]}
                            onChange={handleCheckboxChange(equipment.uuid)}
                            sx={{ color: 'primary.main' }} // Use primary color for checkbox
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 'bold', color: 'text.primary' }}
                            >
                              {equipment?.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 1, display: 'inline-block' }}
                            >
                              ({equipment?.equipmentType?.name || 'N/A'})
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              VIN: {equipment?.vin || 'N/A'} | License:{' '}
                              {equipment?.licensePlate || 'N/A'}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  ))}
                </FormGroup>
              ) : (
                <Box
                  sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}
                >
                  <Typography variant="body2">
                    {searchTerm
                      ? 'No equipment matches your search criteria.'
                      : 'No equipment available.'}
                  </Typography>
                </Box>
              )}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button
          onClick={handleClose}
          color="error"
          variant="outlined"
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssignEquipment}
          variant="contained"
          color="primary"
          disabled={
            Object.keys(selectedEquipment).filter(
              (uuid) => selectedEquipment[uuid]
            ).length === 0
          }
        >
          Assign Equipment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
